import { Button, Modal } from '@evaneos/b2b-ui/legacy';
import { Formik, Form, FieldArray } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PENDING } from '../../../../../../design-system/constants';
import { Add } from '../../../../../../design-system/Icons/Add';
import { SidePanelViewMode } from '../../../../SidePanel/types';
import {
    formatTravelers,
    travelerStepValidationSchema,
} from '../../../../../../resources/contract/operations';
import { postContractAPICallStatus } from '../../../../../../resources/contract/selectors';
import { ContractEditStep, Traveler } from '../../../../../../resources/contract/types';
import { useAppSelector } from '../../../../../../shared/redux';
import styleContract from '../../../Contract.css';
import { StepButtons } from '../StepButtons';

import { TravelerComponent } from './Traveler';
import s from './TravelersStep.css';

type Props = {
    travelers: Traveler[];
    saveTravelers: (travelers: Traveler[]) => void;
    viewMode?: SidePanelViewMode;
};

export const TravelersStep = ({ travelers, saveTravelers, viewMode }: Props): JSX.Element => {
    const intl = useIntl();
    const postContractStatus = useAppSelector(postContractAPICallStatus);
    const [isDeleteTravelerModalOpened, setIsDeleteTravelerModalOpened] = useState(false);
    const [travelerToDelete, setTravelerToDelete] = useState(0);

    return (
        <div
            className={s.travelersStepContainer}
            {...(viewMode && { 'data-contract-view': viewMode })}
        >
            <p className={styleContract.stepSubtitle}>
                <FormattedMessage
                    id="contract.step_subtitle"
                    values={{
                        step: ContractEditStep.TRAVELERS,
                    }}
                />
            </p>
            <h1 className="title-m">
                <FormattedMessage id="contract.travelers_information.title" />
            </h1>
            <Formik
                initialValues={{
                    travelers: formatTravelers(travelers, '-', '/'),
                }}
                validationSchema={travelerStepValidationSchema}
                onSubmit={(values) => {
                    if (values.travelers.length !== 0) {
                        saveTravelers(formatTravelers(values.travelers, '/', '-'));
                    }
                }}
            >
                {({ values, isValid }) => (
                    <Form className={s.formContainer}>
                        <div className={s.headersTable}>
                            <div className="ink">
                                <FormattedMessage id="contract.travelers_information.first_name" />
                            </div>
                            <div className="ink">
                                <FormattedMessage id="contract.travelers_information.last_name" />
                            </div>
                            <div className="ink">
                                <FormattedMessage id="contract.travelers_information.country" />
                            </div>
                            <div className="ink">
                                <FormattedMessage id="contract.travelers_information.birth_date" />
                            </div>
                        </div>
                        <FieldArray name="travelers">
                            {(arrayHelpers) => (
                                <>
                                    <div className={s.lineContainer}>
                                        {values.travelers &&
                                            values.travelers.length > 0 &&
                                            values.travelers.map((traveler, index) => (
                                                <TravelerComponent
                                                    key={index}
                                                    index={index}
                                                    isDeleteTravelerModalOpened={
                                                        setIsDeleteTravelerModalOpened
                                                    }
                                                    travelerIndexToDelete={setTravelerToDelete}
                                                    viewMode={viewMode}
                                                />
                                            ))}
                                    </div>
                                    <Modal
                                        isOpen={isDeleteTravelerModalOpened}
                                        title={intl.formatMessage({
                                            id: 'contract.travelers_information.delete.title',
                                        })}
                                        onRequestClose={() => setIsDeleteTravelerModalOpened(false)}
                                        mainButton={
                                            <Button
                                                danger
                                                onClick={() => {
                                                    arrayHelpers.remove(travelerToDelete);
                                                    setIsDeleteTravelerModalOpened(false);
                                                }}
                                                data-testid="modal.delete.traveler"
                                            >
                                                <FormattedMessage id="contract.travelers_information.delete.button" />
                                            </Button>
                                        }
                                        secondaryButton={
                                            <Button
                                                priority="secondary"
                                                onClick={() =>
                                                    setIsDeleteTravelerModalOpened(false)
                                                }
                                            >
                                                <FormattedMessage id="form.cancel" />
                                            </Button>
                                        }
                                    >
                                        <p>
                                            <FormattedMessage id="contract.travelers_information.delete.message" />
                                        </p>
                                    </Modal>
                                    <Button
                                        priority="secondary"
                                        onClick={() =>
                                            arrayHelpers.push({
                                                firstName: '',
                                                lastName: '',
                                                country: '',
                                                birthDate: '',
                                            })
                                        }
                                        type="button"
                                        className={s.addTravelerButton}
                                    >
                                        <Add color="var(--ink)" />
                                        <FormattedMessage id="contract.travelers_information.button.add" />
                                    </Button>
                                </>
                            )}
                        </FieldArray>
                        <StepButtons
                            isNextStepDisabled={!isValid}
                            isLoading={postContractStatus === PENDING}
                            dataTestId="button-submit-step-2"
                            viewMode={viewMode}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
};
